/**
 * The export dialog.
 *
 * It stays open for the whole batch because a full-resolution export is slow
 * enough that the photographer needs to see where it has got to, and because a
 * failure on photo nineteen has to be readable after the fact rather than
 * vanishing into a toast.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../state/store.ts';
import { Slider } from './controls/Slider.tsx';
import {
  DEFAULT_EXPORT_OPTIONS,
  FORMAT_LABEL,
  type BatchProgress,
  type ExportFormat,
  type ExportOptions,
  type ExportResult,
  downloadBlob,
  exportDimensions,
  exportFilename,
  exportMany,
  exportSidecar,
  formatBytes,
  sidecarFilename,
} from '../export/exporter.ts';

type Scope = 'photo' | 'selection' | 'session';
type EdgeChoice = 'full' | '4096' | '2560' | '2048' | '1024' | 'custom';

const EDGE_CHOICES: { value: EdgeChoice; label: string; edge: number | null }[] = [
  { value: 'full', label: 'Full size', edge: null },
  { value: '4096', label: '4096 px, large print', edge: 4096 },
  { value: '2560', label: '2560 px, gallery', edge: 2560 },
  { value: '2048', label: '2048 px, client proof', edge: 2048 },
  { value: '1024', label: '1024 px, social', edge: 1024 },
  { value: 'custom', label: 'Custom', edge: null },
];

const FORMATS: ExportFormat[] = ['jpeg', 'png', 'webp'];

const MIN_CUSTOM_EDGE = 256;
const MAX_CUSTOM_EDGE = 20000;

export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ExportDialog({ open, onClose }: ExportDialogProps) {
  const photos = useStore((state) => state.photos);
  const selectedId = useStore((state) => state.selectedId);
  const selection = useStore((state) => state.selection);
  const notify = useStore((state) => state.notify);

  const [scope, setScope] = useState<Scope>('photo');
  const [format, setFormat] = useState<ExportFormat>(DEFAULT_EXPORT_OPTIONS.format);
  const [quality, setQuality] = useState(DEFAULT_EXPORT_OPTIONS.quality);
  const [edgeChoice, setEdgeChoice] = useState<EdgeChoice>('full');
  const [customEdge, setCustomEdge] = useState('3000');
  const [suffix, setSuffix] = useState(DEFAULT_EXPORT_OPTIONS.filenameSuffix);
  const [withSidecar, setWithSidecar] = useState(false);

  const [running, setRunning] = useState(false);
  const [cancelRequested, setCancelRequested] = useState(false);
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const [results, setResults] = useState<ExportResult[]>([]);

  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef(false);
  const runningRef = useRef(false);

  const targets = useMemo(() => {
    if (scope === 'session') return photos;
    if (scope === 'selection') return photos.filter((photo) => selection.includes(photo.id));
    return photos.filter((photo) => photo.id === selectedId);
  }, [scope, photos, selection, selectedId]);

  const parsedCustom = Number.parseInt(customEdge, 10);
  const customValid =
    Number.isFinite(parsedCustom) &&
    parsedCustom >= MIN_CUSTOM_EDGE &&
    parsedCustom <= MAX_CUSTOM_EDGE;

  const longEdge =
    edgeChoice === 'custom'
      ? customValid
        ? parsedCustom
        : null
      : (EDGE_CHOICES.find((choice) => choice.value === edgeChoice)?.edge ?? null);

  const options: ExportOptions = useMemo(
    () => ({ format, quality, longEdge, filenameSuffix: suffix }),
    [format, quality, longEdge, suffix],
  );

  // The readout is honest about which photo it describes: crops differ, so a
  // batch has no single output size.
  const sample = useMemo(() => targets.find((photo) => photo.image) ?? null, [targets]);
  const size = sample ? exportDimensions(sample, options) : null;
  const resultById = useMemo(
    () => new Map(results.map((result) => [result.photoId, result])),
    [results],
  );

  useEffect(() => {
    if (!open) return;
    setResults([]);
    setProgress(null);
    setCancelRequested(false);
    dialogRef.current?.querySelector<HTMLElement>('select, input, button')?.focus();
  }, [open]);

  const run = useCallback(async () => {
    if (runningRef.current || targets.length === 0) return;
    runningRef.current = true;
    cancelRef.current = false;
    setCancelRequested(false);
    setResults([]);
    setProgress(null);
    setRunning(true);

    // Freeze the inputs for the duration: decoding a photo mid-batch rewrites
    // the store, and the run must not change shape underneath itself.
    const batch = targets;
    const batchOptions = options;
    const saveSidecar = withSidecar;

    const finished = await exportMany(batch, batchOptions, (value) => setProgress(value), {
      shouldCancel: () => cancelRef.current,
      onPhoto: (result) => {
        if (result.status === 'ok' && result.blob) {
          downloadBlob(result.blob, result.outputFilename);
          if (saveSidecar) {
            const photo = batch.find((entry) => entry.id === result.photoId);
            if (photo) downloadBlob(exportSidecar(photo), sidecarFilename(photo, batchOptions));
          }
        }
        setResults((current) => [...current, { ...result, blob: undefined }]);
      },
    });

    setResults(finished);
    setProgress(null);
    setRunning(false);
    runningRef.current = false;

    const exported = finished.filter((result) => result.status === 'ok').length;
    const failed = finished.filter((result) => result.status === 'failed').length;
    notify(
      failed > 0
        ? `Exported ${exported} of ${finished.length}. ${failed} failed.`
        : `Exported ${exported} ${exported === 1 ? 'photo' : 'photos'}.`,
      failed > 0 ? 'error' : 'info',
    );
  }, [targets, options, withSidecar, notify]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      if (running) return;
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const node = dialogRef.current;
    if (!node) return;
    const focusable = Array.from(
      node.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const exported = results.filter((result) => result.status === 'ok').length;
  const failures = results.filter((result) => result.status === 'failed');
  const stopped = results.filter((result) => result.status === 'cancelled').length;
  const canExport = targets.length > 0 && (edgeChoice !== 'custom' || customValid);
  const showList = running || results.length > 0;

  return (
    <div
      className="scrim"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !running) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        className="dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-title"
      >
        <div className="dialog-head">
          <div className="dialog-title" id="export-title">
            Export
          </div>
        </div>

        <div className="dialog-body">
          <p className="slider-help" style={{ margin: 0, padding: 0 }}>
            Each file is re-decoded at full resolution, so expect a few seconds per photo. Your
            browser may ask permission to save more than one.
          </p>

          <div className="field">
            <label className="label" htmlFor="export-scope">
              Photos
            </label>
            <select
              id="export-scope"
              value={scope}
              disabled={running}
              onChange={(event) => setScope(event.target.value as Scope)}
            >
              <option value="photo">Selected photo</option>
              <option value="selection">
                All {selection.length} selected in the filmstrip
              </option>
              <option value="session">All {photos.length} in the session</option>
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="export-format">
              Format
            </label>
            <select
              id="export-format"
              value={format}
              disabled={running}
              onChange={(event) => setFormat(event.target.value as ExportFormat)}
            >
              {FORMATS.map((value) => (
                <option key={value} value={value}>
                  {FORMAT_LABEL[value]}
                  {value === 'png' ? ' (lossless, large files)' : ''}
                </option>
              ))}
            </select>
          </div>

          {format === 'png' ? null : (
            <Slider
              label="Quality"
              value={quality}
              min={40}
              max={100}
              step={1}
              neutral={DEFAULT_EXPORT_OPTIONS.quality}
              unit="%"
              disabled={running}
              help="92 holds up for client delivery and print. Below about 80 the compression starts to show on skin and smooth sky gradients."
              onChange={setQuality}
            />
          )}

          <div className="field">
            <label className="label" htmlFor="export-edge">
              Long edge
            </label>
            <select
              id="export-edge"
              value={edgeChoice}
              disabled={running}
              onChange={(event) => setEdgeChoice(event.target.value as EdgeChoice)}
            >
              {EDGE_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </div>

          {edgeChoice === 'custom' ? (
            <div className="field">
              <label className="label" htmlFor="export-custom-edge">
                Custom long edge in pixels
              </label>
              <input
                id="export-custom-edge"
                type="number"
                inputMode="numeric"
                min={MIN_CUSTOM_EDGE}
                max={MAX_CUSTOM_EDGE}
                step={1}
                value={customEdge}
                disabled={running}
                aria-invalid={!customValid}
                aria-describedby="export-custom-help"
                onChange={(event) => setCustomEdge(event.target.value)}
              />
              <span className="slider-help" id="export-custom-help" style={{ padding: 0 }}>
                {customValid
                  ? 'Photos already smaller than this are exported at their own size; nothing is enlarged.'
                  : `Enter a number between ${MIN_CUSTOM_EDGE} and ${MAX_CUSTOM_EDGE}.`}
              </span>
            </div>
          ) : null}

          <div className="field">
            <label className="label" htmlFor="export-suffix">
              Filename suffix
            </label>
            <input
              id="export-suffix"
              type="text"
              value={suffix}
              disabled={running}
              spellCheck={false}
              onChange={(event) => setSuffix(event.target.value)}
            />
          </div>

          <label
            className="field"
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
          >
            <input
              type="checkbox"
              checked={withSidecar}
              disabled={running}
              onChange={(event) => setWithSidecar(event.target.checked)}
              style={{ accentColor: 'var(--ember)', width: 13, height: 13, margin: 0 }}
            />
            <span>Also save the .lumos.json recipe</span>
          </label>
          <span className="slider-help" style={{ padding: 0, marginTop: -8 }}>
            A readable record of every setting used, saved next to the image. The original file is
            never modified.
          </span>

          <hr className="dashed-rule" />

          <div className="scope-readout">
            <b>Output</b>
            <span>
              {size ? `${size.width} × ${size.height} px` : 'known once the file is decoded'}
              {size && !size.resized ? ' · full size' : ''}
            </span>
          </div>
          <div className="scope-readout">
            <b>First file</b>
            <span
              style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {targets.length > 0 ? exportFilename(targets[0], options) : 'nothing selected'}
            </span>
          </div>
          {targets.length > 1 && size ? (
            <span className="slider-help" style={{ padding: 0 }}>
              Measured from {sample?.filename}. Photos with a different crop or orientation come out
              at their own dimensions.
            </span>
          ) : null}
          {targets.length === 0 ? (
            <span className="slider-help" style={{ padding: 0 }}>
              Nothing selected to export.
            </span>
          ) : null}

          {showList ? (
            <div className="field">
              <span className="label">Progress</span>
              <ul
                aria-live="polite"
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  maxHeight: 160,
                  overflowY: 'auto',
                }}
              >
                {targets.map((photo) => {
                  const result = resultById.get(photo.id);
                  const active = !result && progress?.photoId === photo.id;
                  const state = result
                    ? result.status === 'ok'
                      ? `Saved · ${formatBytes(result.bytes ?? 0)}`
                      : result.status === 'failed'
                        ? 'Failed'
                        : 'Not started'
                    : active
                      ? (progress?.message ?? 'Working')
                      : running
                        ? 'Waiting'
                        : 'Queued';
                  return (
                    <li key={photo.id} className="scope-readout" style={{ padding: 0 }}>
                      <b
                        style={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {photo.filename}
                      </b>
                      <span
                        className={result?.status === 'failed' ? 'clip-warn' : undefined}
                        style={{ flex: '0 0 auto' }}
                      >
                        {state}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {!running && results.length > 0 ? (
            <div className="field">
              <span className="label">Result</span>
              <span className="slider-help" style={{ padding: 0 }}>
                Exported {exported} of {results.length}
                {stopped > 0 ? `, stopped before ${stopped}` : ''}.
              </span>
              {failures.map((failure) => (
                <span className="warning caution" key={failure.photoId}>
                  {failure.filename}: {failure.error}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="dialog-foot">
          {running ? (
            <>
              <button
                type="button"
                className="btn"
                disabled={cancelRequested}
                onClick={() => {
                  cancelRef.current = true;
                  setCancelRequested(true);
                }}
              >
                {cancelRequested ? 'Stopping' : 'Stop after this photo'}
              </button>
              <button type="button" className="btn ember" disabled>
                Exporting
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn quiet" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="btn ember"
                disabled={!canExport}
                onClick={() => void run()}
              >
                Export {targets.length} {targets.length === 1 ? 'photo' : 'photos'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
